import { useState, useEffect, useRef } from "react";
import { supabase } from "./db";

const GLOBAL_SECTIONS = [
  { id: 0, name: "Základné identifikátory", icon: "🏷️" },
  { id: 1, name: "Produktový obsah a marketing", icon: "💡" },
  { id: 2, name: "Logistické údaje", icon: "📦" },
  { id: 3, name: "Obchodné podmienky", icon: "💶" },
];

// 👇 TOTO JE KĽÚČOVÁ OPRAVA: Prepojenie tvojich starých stĺpcov v DB s novými dynamickými názvami
const SYSTEM_COLUMNS_MAP = {
  brand: "brand",
  produktova_rada: "product_line",
  sukl: "sukl",
  ean_kod: "ean", // Tu systém pochopí, že "ean_kod" má uložiť do stĺpca "ean"
  adc_kod: "adc",
  predchodca: "predecessor",
  vyrobca: "manufacturer",
  krajina_povodu: "country_of_origin",
  kratky_text: "short_description",
  hlavny_popis: "description",
  zlozenie_produktu: "ingredients",
  davkovanie: "dosage",
  kod_colneho_sadzobnika: "customs_code",
  sirka_mm: "width_mm",
  vyska_mm: "height_mm",
  hlbka_mm: "depth_mm",
  vaha_brutto_g: "weight_brutto_g",
  vaha_netto_g: "weight_netto_g",
  pocet_ks_v_kartone: "pieces_in_carton",
  pocet_ks_na_palete: "pieces_on_pallet",
  balenie: "packaging",
  hmotnost_celkova: "total_weight",
  cena_vyrobcu: "price",
  _dph: "vat_percent",
  dmoc: "dmoc",
  zalohovany_obal: "returnable_packaging",
  recyklacny_poplatok: "recycling_fee",
  typ_listingu: "listing_type",
  admin_poplatok: "admin_fee",
};

export default function ManualListing({ session, onBack }) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [currentSection, setCurrentSection] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);

  const [categoriesList, setCategoriesList] = useState([]);
  const [globalAttributes, setGlobalAttributes] = useState([]);
  const [categoryAttributes, setCategoryAttributes] = useState([]);

  const [dynamicValues, setDynamicValues] = useState({
    product_name: "",
    category: "",
  });

  const sanitizeColumnName = (name) => {
    return name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    fetchInitialData();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchInitialData = async () => {
    const [{ data: catData }, { data: globData }] = await Promise.all([
      supabase.from("categories").select("name").order("name"),
      supabase.from("global_attributes").select("*").order("created_at"),
    ]);
    if (catData) setCategoriesList(catData.map((c) => c.name));
    if (globData) setGlobalAttributes(globData);
  };

  const handleCategoryChange = async (e) => {
    const newCategory = e.target.value;
    setDynamicValues((prev) => ({ ...prev, category: newCategory }));

    if (newCategory) {
      const { data } = await supabase
        .from("category_attributes")
        .select("*")
        .eq("category_name", newCategory)
        .order("created_at");
      setCategoryAttributes(data || []);
    } else {
      setCategoryAttributes([]);
    }
  };

  const handleDynamicValueChange = (colName, value) => {
    setDynamicValues((prev) => ({ ...prev, [colName]: value }));
  };

  const validateImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => resolve(img.width >= 800 && img.height >= 800);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setError("");

    try {
      if (!dynamicValues.product_name) throw new Error("Chýba názov produktu!");
      if (!dynamicValues.category) throw new Error("Chýba kategória!");
      if (!imageFile) throw new Error("Musíte vybrať obrázok.");

      const fileExt = imageFile.name.split(".").pop();
      const filePath = `${session.user.id}/${Math.random()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from("product-images")
        .upload(filePath, imageFile);
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage
        .from("product-images")
        .getPublicUrl(filePath);

      // 👇 SPRACOVANIE OBJEKTU: Dynamické hodnoty rozdelíme na systémové stĺpce a skutočné dynamické stĺpce
      const processedValues = {};

      Object.keys(dynamicValues).forEach((key) => {
        // Ak je kľúč v mape, použijeme reálny názov z databázy (napr. ean_kod -> ean)
        if (SYSTEM_COLUMNS_MAP[key]) {
          const realDbColumn = SYSTEM_COLUMNS_MAP[key];

          // Konverzia číselných polí pre databázu
          const numberCols = [
            "price",
            "width_mm",
            "height_mm",
            "depth_mm",
            "weight_brutto_g",
            "weight_netto_g",
            "recycling_fee",
            "vat_percent",
            "dmoc",
          ];
          const intCols = ["pieces_in_carton", "pieces_on_pallet"];

          if (numberCols.includes(realDbColumn)) {
            processedValues[realDbColumn] =
              parseFloat(dynamicValues[key]) || null;
          } else if (intCols.includes(realDbColumn)) {
            processedValues[realDbColumn] =
              parseInt(dynamicValues[key]) || null;
          } else {
            processedValues[realDbColumn] = dynamicValues[key];
          }
        } else {
          // Ak nie je v mape, je to vlastný dynamický stĺpec
          processedValues[key] = dynamicValues[key];
        }
      });

      const finalProductData = {
        supplier_id: session.user.id,
        image_url: urlData.publicUrl,
        stock: 0,
        status: "Čaká na schválenie",
        ...processedValues, // Vložíme správne zmapované hodnoty
      };

      const { error: insertError } = await supabase
        .from("products")
        .insert([finalProductData]);
      if (insertError) throw insertError;

      try {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("company_name")
          .eq("id", session.user.id)
          .single();
        await supabase.from("supplier_logs").insert([
          {
            supplier_email: session.user.email,
            supplier_name: profileData?.company_name || "Neznáma firma",
            action: `Zalistovaný nový produkt: ${dynamicValues.product_name}`,
          },
        ]);
      } catch (logErr) {
        console.error("Chyba logu:", logErr);
      }

      setMessage("Produkt bol úspešne zalistovaný!");
      setTimeout(() => onBack(), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, clientHeight } = containerRef.current;
      const sectionIndex = Math.round(scrollTop / clientHeight);
      if (sectionIndex !== currentSection) setCurrentSection(sectionIndex);
    }
  };

  const scrollToSection = (index) => {
    if (containerRef.current) {
      const clientHeight = containerRef.current.clientHeight;
      containerRef.current.scrollTo({
        top: index * clientHeight,
        behavior: "smooth",
      });
    }
  };

  const s = getStyles(isMobile, currentSection);

  const renderFields = (attributesList) => {
    return attributesList.map((attr) => {
      const colName = sanitizeColumnName(attr.attribute_name);
      const isBaseName =
        attr.attribute_name.toLowerCase().includes("názov produktu") ||
        attr.attribute_name.toLowerCase().includes("nazov produktu");
      const finalColName = isBaseName ? "product_name" : colName;

      return (
        <div style={s.formGroup} key={attr.id}>
          <label
            style={{
              ...s.label,
              fontWeight: attr.is_required ? "800" : "600",
              color: "#1e3a8a",
            }}
          >
            {attr.attribute_name}{" "}
            {attr.is_required && <span style={{ color: "#dc2626" }}>*</span>}
          </label>

          {attr.attribute_type === "select" ? (
            <select
              value={dynamicValues[finalColName] || ""}
              onChange={(e) =>
                handleDynamicValueChange(finalColName, e.target.value)
              }
              required={attr.is_required}
              style={s.input}
            >
              <option value="">-- Vyberte --</option>
              {attr.options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : attr.attribute_type === "textarea" ? (
            <textarea
              value={dynamicValues[finalColName] || ""}
              onChange={(e) =>
                handleDynamicValueChange(finalColName, e.target.value)
              }
              required={attr.is_required}
              rows="3"
              style={s.textarea}
            />
          ) : (
            <input
              type={attr.attribute_type === "number" ? "number" : "text"}
              value={dynamicValues[finalColName] || ""}
              onChange={(e) =>
                handleDynamicValueChange(finalColName, e.target.value)
              }
              required={attr.is_required}
              style={s.input}
            />
          )}
        </div>
      );
    });
  };

  const SectionHeader = ({ title, emoji }) => (
    <div style={s.sectionHeader}>
      <span style={s.sectionEmoji}>{emoji}</span>
      <h3 style={s.sectionTitle}>{title}</h3>
    </div>
  );

  return (
    <div style={s.mainWrapper}>
      {!isMobile && (
        <div style={s.paginationContainer}>
          {GLOBAL_SECTIONS.map((item) => (
            <div
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              style={s.dotWrapper}
              title={item.name}
            >
              <div
                style={{
                  ...s.dot,
                  backgroundColor:
                    currentSection === item.id
                      ? "#3b82f6"
                      : "rgba(148, 163, 184, 0.4)",
                  transform:
                    currentSection === item.id ? "scale(1.5)" : "scale(1)",
                }}
              />
            </div>
          ))}
        </div>
      )}
      <style>{`::-webkit-scrollbar { display: none; }`}</style>

      <div style={s.glassCard}>
        <div style={s.cardHeader}>
          <button onClick={onBack} style={s.backBtn}>
            ← Späť na výber
          </button>
          <div>
            <h2 style={s.mainHeading}>Dynamický formulár</h2>
            <p style={s.mainSubtitle}>
              Polia sú generované automaticky na základe požiadaviek nákupcov.
            </p>
          </div>
        </div>

        {error && <div style={s.errorAlert}>🚨 {error}</div>}
        {message && <div style={s.successAlert}>✅ {message}</div>}

        <form
          onSubmit={handleSubmit}
          style={s.formScrollContainer}
          ref={containerRef}
          onScroll={handleScroll}
        >
          {GLOBAL_SECTIONS.map((section) => {
            const sectionFields = globalAttributes.filter(
              (a) => a.section_name === section.name
            );
            return (
              <div style={s.formBlock} key={section.id}>
                <div style={s.blockContent}>
                  <SectionHeader title={section.name} emoji={section.icon} />
                  <div style={s.gridStyle}>
                    {renderFields(sectionFields)}

                    {section.name === "Produktový obsah a marketing" && (
                      <>
                        <div style={s.formGroup}>
                          <label
                            style={{
                              ...s.label,
                              fontWeight: "800",
                              color: "#1e3a8a",
                            }}
                          >
                            Kategória produktu *
                          </label>
                          <select
                            required
                            value={dynamicValues.category || ""}
                            onChange={handleCategoryChange}
                            style={s.input}
                          >
                            <option value="">-- Zvoľte kategóriu --</option>
                            {categoriesList.map((cat) => (
                              <option key={cat} value={cat}>
                                {cat}
                              </option>
                            ))}
                          </select>
                        </div>
                        {categoryAttributes.length > 0 && (
                          <div
                            style={{
                              ...s.dynamicAttributesBox,
                              gridColumn: "1 / -1",
                            }}
                          >
                            <h4
                              style={{ margin: "0 0 15px 0", color: "#1d4ed8" }}
                            >
                              ✨ Parametre pre kategóriu:{" "}
                              {dynamicValues.category}
                            </h4>
                            <div style={s.gridStyle}>
                              {renderFields(categoryAttributes)}
                            </div>
                          </div>
                        )}
                        <div
                          style={{
                            ...s.formGroup,
                            gridColumn: "1 / -1",
                            marginTop: "10px",
                          }}
                        >
                          <label
                            style={{
                              ...s.label,
                              fontWeight: "800",
                              color: "#1e3a8a",
                            }}
                          >
                            Obrázok produktu *
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            required
                            onChange={(e) => setImageFile(e.target.files[0])}
                            style={{ ...s.input, padding: "10px" }}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {section.id === 3 && (
                    <button
                      type="submit"
                      disabled={loading}
                      style={{ ...s.submitButton, marginTop: "40px" }}
                    >
                      {loading ? "Ukladám..." : "🚀 Uložiť a odoslať produkt"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </form>
      </div>
    </div>
  );
}

const getStyles = (isMobile, currentSection) => ({
  mainWrapper: {
    width: "100%",
    animation: "fadeIn 0.4s ease-out",
    position: "relative",
  },
  paginationContainer: {
    position: "absolute",
    left: "-40px",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    zIndex: 100,
  },
  dotWrapper: { padding: "5px", cursor: "pointer" },
  dot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  glassCard: {
    backgroundColor: "rgba(255, 255, 255, 0.65)",
    backdropFilter: "blur(24px)",
    borderRadius: "24px",
    border: "1px solid rgba(255, 255, 255, 0.8)",
    boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.08)",
    height: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "30px 40px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.5)",
    display: "flex",
    gap: "20px",
    alignItems: "center",
  },
  backBtn: {
    background: "rgba(255, 255, 255, 0.9)",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    padding: "10px 16px",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "600",
  },
  mainHeading: { margin: "0 0 5px 0", fontSize: "24px", color: "#0f172a" },
  mainSubtitle: { margin: 0, color: "#64748b" },
  formScrollContainer: { flex: 1, overflowY: "auto", padding: "0 40px" },
  formBlock: { padding: "40px 0" },
  blockContent: {
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    padding: "40px",
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.8)",
    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "25px",
    borderBottom: "1px solid #e2e8f0",
    paddingBottom: "15px",
  },
  sectionEmoji: { fontSize: "24px" },
  sectionTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: "#0f172a",
  },
  gridStyle: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "20px",
  },
  formGroup: { display: "flex", flexDirection: "column", gap: "8px" },
  label: { fontSize: "13px" },
  input: {
    padding: "12px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    backgroundColor: "white",
    outline: "none",
  },
  textarea: {
    padding: "12px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    backgroundColor: "white",
    outline: "none",
    resize: "vertical",
  },
  dynamicAttributesBox: {
    backgroundColor: "rgba(239, 246, 255, 0.6)",
    padding: "20px",
    borderRadius: "16px",
    border: "1px dashed #93c5fd",
    marginTop: "15px",
  },
  submitButton: {
    width: "100%",
    padding: "18px",
    backgroundColor: "#10b981",
    color: "white",
    borderRadius: "16px",
    border: "none",
    fontWeight: "bold",
    cursor: "pointer",
  },
  errorAlert: {
    margin: "20px",
    padding: "15px",
    backgroundColor: "#fef2f2",
    color: "#991b1b",
    borderRadius: "12px",
  },
  successAlert: {
    margin: "20px",
    padding: "15px",
    backgroundColor: "#f0fdf4",
    color: "#166534",
    borderRadius: "12px",
  },
});
